import React, { useEffect, useState, useContext } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { abbreviateAmount, formatMoney } from '../../data/formatting';
import { CognitoAuthContext } from '../../context/CognitoAuthContext';

const ConfirmTrade = ({ tradeData }) => {
  const tradesApiUrl = process.env.REACT_APP_TRADES_API;
  const logoUrl = `${process.env.PUBLIC_URL || ''}/logo-black.png`;
  const { user } = useContext(CognitoAuthContext);
  const [entity, setEntity] = useState(tradeData.entity);
  const [cpty, setCpty] = useState(tradeData.cpty);
  const [parties, setParties] = useState({});
  const [loading, setLoading] = useState(true);

  const displayIsin =
    tradeData.isin ||
    tradeData.ISIN ||
    tradeData.bondIsin ||
    tradeData.bondISIN ||
    '';

  const displayBondName = tradeData.bondName || displayIsin;
  const underlyingCcy = tradeData.bondCcy || tradeData.bondCurrency || tradeData.bond_ccy || tradeData.ccy || '';
  const tradeId = tradeData.tradeId || tradeData.id || tradeData.tradeRef || '';
  const tradeStatus = tradeData.status || '';
  const tradeDateValue = tradeData.tradeDate
    || (tradeData.timestamp ? new Date(Number(tradeData.timestamp)).toISOString().slice(0, 10) : '');
  const traderName = tradeData.traderName || user?.name || '';
  const traderEmail = tradeData.traderEmail || user?.email || '';

  // Load parties and counterparties from APIs
  const loadParties = async () => {
    try {
      setLoading(true);

      const [partiesRes, cptsRes] = await Promise.all([
        fetch(`${tradesApiUrl}/parties`),
        fetch(`${tradesApiUrl}/counterparties`)
      ]);

      const [partiesData, cptsData] = await Promise.all([
        partiesRes.json(),
        cptsRes.json()
      ]);

      // Combine parties and counterparties
      const allParties = {};
      partiesData.items?.forEach(item => {
        allParties[item.partyId] = {
          name: item.name,
          address: item.address || '',
          city: item.city || '',
          postcode: item.postcode || '',
          country: item.country || '',
          phone: item.phone || ''
        };
      });
      cptsData.items?.forEach(item => {
        allParties[item.counterpartyId] = { name: item.name };
      });
      setParties(allParties);
      setLoading(false);
    } catch (error) {
      console.error('Error loading parties:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tradesApiUrl) {
      loadParties();
    }
  }, [tradesApiUrl]);

  // Update entity and cpty when parties or tradeData changes
  useEffect(() => {
    const entityParty = parties[tradeData.entity] || { name: tradeData.entity };
    const cptyParty = parties[tradeData.cpty] || { name: tradeData.cpty };
    setEntity(entityParty);
    setCpty(cptyParty);
  }, [tradeData, parties]);

  const traderAddress = [entity?.address, entity?.city, entity?.postcode, entity?.country]
    .filter(Boolean)
    .join(', ');
  const traderPhone = entity?.phone || '';

  const pdfStyles = StyleSheet.create({
    page: {
      padding: 24,
      fontSize: 10
    },
    logo: {
      width: '50%',
      marginBottom: 12
    },
    title: {
      fontSize: 12,
      marginBottom: 12
    },
    smallText: {
      fontSize: 8,
      color: '#444'
    },
    row: {
      flexDirection: 'row',
      marginBottom: 6
    },
    label: {
      width: '30%',
      fontWeight: 'bold'
    },
    value: {
      width: '70%'
    },
    highlight: {
      backgroundColor: '#FFEB3B',
      padding: 2
    }
  });

  const TradeConfirmPdf = (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <Image style={pdfStyles.logo} src={logoUrl} />
        <Text style={pdfStyles.title}>
          REPO TRADE CONFIRMATION{tradeId ? ` - ${tradeId}` : ''}
        </Text>

        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Status</Text>
          <Text style={pdfStyles.value}>{tradeStatus || '-'}</Text>
        </View>

        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Sukuk</Text>
          <Text style={pdfStyles.value}>{displayBondName}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Underlying Ccy</Text>
          <Text style={pdfStyles.value}>{underlyingCcy || '-'}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>ISIN</Text>
          <Text style={pdfStyles.value}>{displayIsin}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Term</Text>
          <Text style={pdfStyles.value}>{tradeData.term}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Type</Text>
          <Text style={pdfStyles.value}>{tradeData.tradeType}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Start Date</Text>
          <Text style={pdfStyles.value}>{tradeData.start}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Days</Text>
          <Text style={pdfStyles.value}>{tradeData.days}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>End Date</Text>
          <Text style={pdfStyles.value}>{tradeData.end}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Repo Rate</Text>
          <Text style={pdfStyles.value}>{tradeData.repoRate}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Entity</Text>
          <Text style={pdfStyles.value}>{entity.name}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Nominal</Text>
          <Text style={pdfStyles.value}>{abbreviateAmount(tradeData.nominal)}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Clean Price</Text>
          <Text style={pdfStyles.value}>{tradeData.cleanPrice}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Haircut (%)</Text>
          <Text style={pdfStyles.value}>{tradeData.haircut}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Cpty</Text>
          <Text style={pdfStyles.value}>{cpty.name}</Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Start Cash</Text>
          <Text style={[pdfStyles.value, pdfStyles.highlight]}>
            {formatMoney(tradeData.startCash)} {tradeData.ccy}
          </Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>Profit</Text>
          <Text style={[pdfStyles.value, pdfStyles.highlight]}>
            {formatMoney(tradeData.interest)} {tradeData.ccy}
          </Text>
        </View>
        <View style={pdfStyles.row}>
          <Text style={pdfStyles.label}>End Cash</Text>
          <Text style={[pdfStyles.value, pdfStyles.highlight]}>
            {formatMoney(tradeData.endCash)} {tradeData.ccy}
          </Text>
        </View>
        <View style={{ marginTop: 10 }}>
          <Text style={pdfStyles.smallText}>Trade Date: {tradeDateValue || '-'}</Text>
          <Text style={pdfStyles.smallText}>Trader: {traderName || '-'}</Text>
          <Text style={pdfStyles.smallText}>Email: {traderEmail || '-'}</Text>
          <Text style={pdfStyles.smallText}>Address: {traderAddress || '-'}</Text>
          <Text style={pdfStyles.smallText}>Phone: {traderPhone || '-'}</Text>
        </View>
      </Page>
    </Document>
  );

  return (
    <Container className="mt-4 p-3 border">
      <Row className="mb-3">
        <Col>
          <img src={logoUrl} alt="Absolute Collateral" style={{ width: '50%', height: 'auto' }} />
        </Col>
        <Col className="d-flex align-items-start justify-content-end">
          <PDFDownloadLink
            document={TradeConfirmPdf}
            fileName={`Trade_confirmation_${tradeId || 'trade'}.pdf`}
          >
            {({ loading: pdfLoading }) => (
              <Button variant="outline-dark" size="sm" disabled={pdfLoading}>
                {pdfLoading ? 'Preparing PDF...' : 'Export PDF'}
              </Button>
            )}
          </PDFDownloadLink>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Sukuk</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{displayBondName}</p>
        </Col>
        <Col md={2}>
          <p><strong>Underlying Ccy</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{underlyingCcy || '-'}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Status</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeStatus || '-'}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>ISIN</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{displayIsin}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Term</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.term}</p>
        </Col>
        <Col md={2}>
          <p><strong>Type</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.tradeType}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Start Date</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.start}</p>
        </Col>
        <Col md={2}>
          <p><strong>Days</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.days}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>End Date</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.end}</p>
        </Col>
        <Col md={2}>
          <p><strong>Repo Rate</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.repoRate}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Entity</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{entity.name}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Nominal</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{abbreviateAmount(tradeData.nominal)}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Clean Price</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.cleanPrice}</p>
        </Col>
        <Col md={2}>
          <p><strong>Haircut (%)</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.haircut}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Cpty</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{cpty.name}</p>
        </Col>
        <Col md={2}>
          <p><strong>FX Rate</strong><br /></p>
        </Col>
        <Col md={4}>
          <p>{tradeData.fx}</p>
          {tradeData.fxpair && (
            <small style={{ color: '#6c757d' }}>
              Rate date: {tradeData.fxDate || 'N/A'}
            </small>
          )}
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Start Cash</strong><br /></p>
        </Col>
        <Col md={4} className="bg-warning text-center">
          <p>{formatMoney(tradeData.startCash)}</p>
        </Col>
        <Col md={1}>
          <p>{tradeData.ccy}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>Profit</strong><br /></p>
        </Col>
        <Col md={4} className="bg-warning text-center">
          <p>{formatMoney(tradeData.interest)}</p>
        </Col>
        <Col md={1}>
          <p>{tradeData.ccy}</p>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <p><strong>End Cash</strong><br /></p>
        </Col>
        <Col md={4} className="bg-warning text-center">
          <p>{formatMoney(tradeData.endCash)}</p>
        </Col>
        <Col md={1}>
          <p>{tradeData.ccy}</p>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <small className="text-muted">
            Trade Date: {tradeDateValue || '-'}<br />
            Trader: {traderName || '-'}<br />
            Email: {traderEmail || '-'}<br />
            Address: {traderAddress || '-'}<br />
            Phone: {traderPhone || '-'}
          </small>
        </Col>
      </Row>
      
    </Container>
  );
};

export default ConfirmTrade;
